import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import { ConfirmDialog, NeoModal, NeoButton, NeoInput, NeoBadge } from "../../../components/Common";
import { MEDIA_IMAGE_DIMENSIONS } from "../constants";

/**
 * MediaFormModal
 * Modal de criação/edição de Mídia do Totem utilizando o NeoModal do Neo Design System.
 * Suporta tipo "single" (imagem única) e "carousel" (múltiplas imagens).
 */
const MediaFormModal = ({ isOpen, toggle, data, onSave, onDelete, isSaving = false }) => {
    const isEditing = !!data?.id;

    const [name, setName] = useState(data?.name || "");
    const [type, setType] = useState(data?.type || "single");
    const [startDate, setStartDate] = useState(data?.startDate || "");
    const [endDate, setEndDate] = useState(data?.endDate || "");
    const [status, setStatus] = useState(data?.status || "active");
    const [slideDuration, setSlideDuration] = useState(data?.slideDuration || 8);

    // Single image
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(data?.imageUrl || null);
    const fileInputRef = useRef(null);

    // Carousel images
    const [carouselFiles, setCarouselFiles] = useState([]); // File[] novos uploads
    const [carouselPreviews, setCarouselPreviews] = useState([]); // { url, isNew, file? }[]
    const carouselInputRef = useRef(null);

    // Sync form state when data changes
    useEffect(() => {
        setName(data?.name || "");
        setType(data?.type || "single");
        setStartDate(data?.startDate || "");
        setEndDate(data?.endDate || "");
        setStatus(data?.status || "active");
        setSlideDuration(data?.slideDuration || 8);

        // Single
        setImageFile(null);
        setImagePreview(data?.imageUrl || null);

        // Carousel — carregar imagens existentes
        setCarouselFiles([]);
        const existingImages = (data?.carouselImages || [])
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(img => ({ url: img.url, isNew: false }));
        setCarouselPreviews(existingImages);
    }, [data]);

    // === Single image handlers ===
    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImageFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setImagePreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    // === Carousel handlers ===
    const handleCarouselAdd = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const newPreviews = [];
        const newFiles = [...carouselFiles];

        files.forEach(file => {
            newFiles.push(file);
            const reader = new FileReader();
            reader.onload = (ev) => {
                newPreviews.push({ url: ev.target.result, isNew: true, file });
                if (newPreviews.length === files.length) {
                    setCarouselPreviews(prev => [...prev, ...newPreviews]);
                    setCarouselFiles(newFiles);
                }
            };
            reader.readAsDataURL(file);
        });

        // Reset input para permitir re-selecionar mesmos arquivos
        if (carouselInputRef.current) carouselInputRef.current.value = "";
    };

    const handleCarouselRemove = (index) => {
        setCarouselPreviews(prev => {
            const updated = [...prev];
            const removed = updated.splice(index, 1)[0];

            // Se era um arquivo novo, remover do carouselFiles também
            if (removed?.isNew && removed?.file) {
                setCarouselFiles(prevFiles => prevFiles.filter(f => f !== removed.file));
            }

            return updated;
        });
    };

    const handleCarouselReorder = (fromIndex, toIndex) => {
        setCarouselPreviews(prev => {
            const updated = [...prev];
            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, moved);
            return updated;
        });
    };

    // === Validação ===
    const isValid = () => {
        if (!name.trim() || !startDate || !endDate) return false;
        if (type === "single" && !imagePreview && !data?.imageUrl) return false;
        if (type === "carousel" && carouselPreviews.length < 2) return false;
        return true;
    };

    // === Submit ===
    const handleSubmit = () => {
        if (!isValid()) return;

        const existingCarouselImages = carouselPreviews
            .filter(p => !p.isNew)
            .map((p, idx) => ({ url: p.url, order: idx }));

        const newCarouselFiles = carouselPreviews
            .filter(p => p.isNew && p.file)
            .map(p => p.file);

        onSave(
            {
                ...data,
                name: name.trim(),
                type,
                startDate,
                endDate,
                status,
                slideDuration: Number(slideDuration) || 8,
                imageUrl: type === "single" ? (data?.imageUrl || null) : null,
                carouselImages: type === "carousel" ? existingCarouselImages : [],
            },
            type === "single" ? imageFile : null,
            type === "carousel" ? newCarouselFiles : null
        );
    };

    const handleConfirmDelete = async () => {
        const confirmed = await ConfirmDialog({
            title: "Excluir Mídia",
            text: `Deseja excluir "${name || "esta mídia"}"? ${type === "carousel" ? "Todas as imagens serão removidas." : "A imagem também será removida."}`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });
        if (confirmed && onDelete) {
            onDelete(data.id, data.name);
            toggle();
        }
    };

    const footerActions = (
        <div className="d-flex w-100 justify-content-between align-items-center">
            <div>
                {isEditing && onDelete && (
                    <NeoButton 
                        variant="outline-danger" 
                        icon="mdi-trash-can-outline"
                        onClick={handleConfirmDelete}
                    >
                        Excluir
                    </NeoButton>
                )}
            </div>
            <div className="d-flex gap-2">
                <NeoButton 
                    variant="outline-secondary" 
                    onClick={toggle}
                >
                    Cancelar
                </NeoButton>
                <NeoButton
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSaving || !isValid()}
                    loading={isSaving}
                >
                    {isEditing ? "Salvar" : "Criar Mídia"}
                </NeoButton>
            </div>
        </div>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            title={isEditing ? "Editar Mídia" : "Nova Mídia"}
            subtitle={isEditing ? "Ajuste os parâmetros da campanha de totem" : "Crie uma nova campanha de totem"}
            headerIcon={isEditing ? "mdi-pencil-outline" : "mdi-plus-circle-outline"}
            size="lg"
            footerActions={footerActions}
        >
            {/* Seletor de Tipo */}
            <div className="mb-4">
                <label className="neo-input-label fw-bold text-muted small text-uppercase mb-2 d-block">
                    Tipo de Mídia <span className="text-danger">*</span>
                </label>
                <div className="d-flex gap-2">
                    <NeoButton
                        variant={type === "single" ? "primary" : "outline-secondary"}
                        className="flex-grow-1 justify-content-center"
                        onClick={() => setType("single")}
                        icon="mdi-image"
                    >
                        Imagem Única
                    </NeoButton>
                    <NeoButton
                        variant={type === "carousel" ? "primary" : "outline-secondary"}
                        className="flex-grow-1 justify-content-center"
                        onClick={() => setType("carousel")}
                        icon="mdi-view-carousel-outline"
                    >
                        Carrossel
                    </NeoButton>
                </div>
            </div>

            <Row>
                {/* Coluna esquerda: Upload de imagem */}
                <Col md={5}>
                    {type === "single" ? (
                        /* === IMAGEM ÚNICA === */
                        <div className="mb-3">
                            <label className="neo-input-label fw-bold text-muted small text-uppercase mb-2 d-block">
                                Imagem <span className="text-danger">*</span>
                            </label>
                            <div
                                className="overflow-hidden bg-light d-flex align-items-center justify-content-center cursor-pointer position-relative"
                                style={{
                                    aspectRatio: `${MEDIA_IMAGE_DIMENSIONS.width}/${MEDIA_IMAGE_DIMENSIONS.height}`,
                                    maxHeight: 350,
                                    width: "100%",
                                    borderRadius: "12px",
                                    border: "2px dashed rgba(72, 39, 18, 0.2)",
                                    backgroundColor: "#fef5ef",
                                    transition: "all 0.2s"
                                }}
                                onClick={() => fileInputRef.current?.click()}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = "var(--color-brand-accent)";
                                    e.currentTarget.style.backgroundColor = "rgba(226, 102, 28, 0.02)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "rgba(72, 39, 18, 0.2)";
                                    e.currentTarget.style.backgroundColor = "#fef5ef";
                                }}
                            >
                                {imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="w-100 h-100"
                                        style={{ objectFit: "cover" }}
                                    />
                                ) : (
                                    <div className="text-center p-3">
                                        <div 
                                            className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
                                            style={{
                                                width: "48px",
                                                height: "48px",
                                                background: "rgba(226, 102, 28, 0.08)",
                                                border: "1px solid rgba(226, 102, 28, 0.15)"
                                            }}
                                        >
                                            <i className="mdi mdi-cloud-upload-outline font-size-22 text-accent" />
                                        </div>
                                        <p className="mb-1 fw-bold font-size-13 text-dark">Clique para enviar</p>
                                        <p className="mb-0 font-size-10 text-muted">
                                            {MEDIA_IMAGE_DIMENSIONS.label}
                                        </p>
                                    </div>
                                )}

                                {imagePreview && (
                                    <div
                                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                        style={{
                                            background: "rgba(0,0,0,0.4)",
                                            opacity: 0,
                                            transition: "opacity 0.2s",
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                                        onMouseLeave={e => e.currentTarget.style.opacity = "0"}
                                    >
                                        <span className="text-white fw-bold font-size-13">
                                            <i className="mdi mdi-camera me-1" /> Trocar imagem
                                        </span>
                                    </div>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="d-none"
                                onChange={handleImageChange}
                            />
                        </div>
                    ) : (
                        /* === CARROSSEL === */
                        <div className="mb-3">
                            <label className="neo-input-label fw-bold text-muted small text-uppercase mb-2 d-block">
                                Imagens do Carrossel <span className="text-danger">*</span>
                                <span className="text-muted fw-normal ms-1">({carouselPreviews.length} imagens)</span>
                            </label>

                            {/* Grid de thumbnails */}
                            <div className="d-flex flex-column gap-2 custom-scrollbar" style={{ maxHeight: 350, overflowY: "auto" }}>
                                {carouselPreviews.map((preview, idx) => (
                                    <div
                                        key={idx}
                                        className="position-relative overflow-hidden bg-light d-flex align-items-center"
                                        style={{ 
                                            height: 80,
                                            borderRadius: "10px",
                                            border: "1.5px solid rgba(72, 39, 18, 0.12)"
                                        }}
                                    >
                                        <img
                                            src={preview.url}
                                            alt={`Slide ${idx + 1}`}
                                            style={{ width: 45, height: 80, objectFit: "cover", flexShrink: 0 }}
                                        />
                                        <div className="flex-grow-1 px-3 d-flex align-items-center justify-content-between">
                                            <span className="fw-bold font-size-12 text-dark neo-font-outfit">
                                                Slide {idx + 1}
                                                {preview.isNew && (
                                                    <NeoBadge color="accent" className="ms-2">
                                                        Novo
                                                    </NeoBadge>
                                                )}
                                            </span>
                                            <div className="d-flex gap-1 align-items-center">
                                                {idx > 0 && (
                                                    <NeoButton
                                                        size="sm"
                                                        circle
                                                        variant="outline-secondary"
                                                        onClick={() => handleCarouselReorder(idx, idx - 1)}
                                                        title="Mover para cima"
                                                        icon="mdi-arrow-up"
                                                    />
                                                )}
                                                {idx < carouselPreviews.length - 1 && (
                                                    <NeoButton
                                                        size="sm"
                                                        circle
                                                        variant="outline-secondary"
                                                        onClick={() => handleCarouselReorder(idx, idx + 1)}
                                                        title="Mover para baixo"
                                                        icon="mdi-arrow-down"
                                                    />
                                                )}
                                                <NeoButton
                                                    size="sm"
                                                    circle
                                                    variant="soft-danger"
                                                    onClick={() => handleCarouselRemove(idx)}
                                                    title="Remover"
                                                    icon="mdi-close"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Botão de adicionar */}
                                <div
                                    className="d-flex align-items-center justify-content-center cursor-pointer"
                                    style={{ 
                                        height: 70, 
                                        borderRadius: "10px",
                                        border: "2px dashed rgba(72, 39, 18, 0.2)",
                                        backgroundColor: "#fef5ef",
                                        transition: "all 0.2s"
                                    }}
                                    onClick={() => carouselInputRef.current?.click()}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = "var(--color-brand-accent)";
                                        e.currentTarget.style.backgroundColor = "rgba(226, 102, 28, 0.02)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = "rgba(72, 39, 18, 0.2)";
                                        e.currentTarget.style.backgroundColor = "#fef5ef";
                                    }}
                                >
                                    <div className="text-center text-dark">
                                        <i className="mdi mdi-plus-circle-outline font-size-22 d-block text-accent" />
                                        <span className="font-size-11 fw-bold">Adicionar imagens</span>
                                    </div>
                                </div>
                            </div>

                            <input
                                ref={carouselInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="d-none"
                                onChange={handleCarouselAdd}
                            />

                            {carouselPreviews.length > 0 && carouselPreviews.length < 2 && (
                                <div className="text-danger font-size-11 mt-2 fw-semibold">
                                    <i className="mdi mdi-alert-circle-outline me-1" />
                                    Adicione pelo menos 2 imagens para o carrossel.
                                </div>
                            )}
                        </div>
                    )}
                </Col>

                {/* Coluna direita: Campos do formulário */}
                <Col md={7}>
                    <NeoInput
                        label="Nome da Mídia"
                        type="text"
                        placeholder="Ex: Promoção de Verão"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mb-3"
                        flat={true}
                    />

                    <Row>
                        <Col md={6}>
                            <NeoInput
                                label="Data de Início"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="mb-3"
                                flat={true}
                            />
                        </Col>
                        <Col md={6}>
                            <NeoInput
                                label="Data de Fim"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="mb-3"
                                flat={true}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={type === "carousel" ? 6 : 12}>
                            <NeoInput
                                label="Status"
                                type="select"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                flat={true}
                                className="mb-4"
                            >
                                <option value="active">Ativa</option>
                                <option value="inactive">Inativa</option>
                            </NeoInput>
                        </Col>
                        {type === "carousel" && (
                            <Col md={6}>
                                <NeoInput
                                    label="Duração por Slide"
                                    type="select"
                                    value={slideDuration}
                                    onChange={(e) => setSlideDuration(Number(e.target.value))}
                                    flat={true}
                                    className="mb-4"
                                >
                                    <option value={5}>5 segundos</option>
                                    <option value={8}>8 segundos (recomendado)</option>
                                    <option value={10}>10 segundos</option>
                                    <option value={15}>15 segundos</option>
                                    <option value={20}>20 segundos</option>
                                </NeoInput>
                            </Col>
                        )}
                    </Row>

                    {/* Info de dimensão */}
                    <div 
                        className="alert d-flex align-items-center gap-2 mb-0 p-3" 
                        style={{ 
                            borderRadius: "10px", 
                            border: "1.5px solid rgba(72, 39, 18, 0.12)", 
                            backgroundColor: "#fef5ef", 
                            color: "#482712" 
                        }}
                    >
                        <i className="mdi mdi-information-outline font-size-18 text-accent flex-shrink-0" />
                        <span className="font-size-12 fw-semibold text-muted" style={{ fontFamily: "Outfit" }}>
                            {type === "carousel"
                                ? <>Cada imagem do carrossel deve ter resolução <strong>{MEDIA_IMAGE_DIMENSIONS.label}</strong>. A duração ideal é de <strong>8 segundos</strong> por slide.</>
                                : <>Resolução recomendada: <strong>{MEDIA_IMAGE_DIMENSIONS.label}</strong> para TV na vertical.</>
                            }
                        </span>
                    </div>
                </Col>
            </Row>
        </NeoModal>
    );
};

MediaFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    data: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool
};

export default MediaFormModal;
